import axios from "axios";
import { readUtm } from "@/lib/utm";
import {
    formatApiError as _formatApiErrorUnified,
    normalizeApiError as _normalizeApiError,
} from "@/lib/apiErrors";

// Prefer the build-time env var, but fall back to the current origin so the app keeps working
// even if REACT_APP_BACKEND_URL was baked with a URL that no longer resolves
// (e.g. a custom domain that hasn't propagated DNS yet).
function resolveBackendUrl() {
    const fromEnv = process.env.REACT_APP_BACKEND_URL;
    if (typeof window === "undefined") return fromEnv || "";
    if (!fromEnv) return window.location.origin;
    try {
        const envHost = new URL(fromEnv).host;
        // If the baked host doesn't match where the app is being served from AND
        // the runtime host is a real reachable origin, prefer runtime.
        // This catches the "REACT_APP_BACKEND_URL was set to an unreachable custom domain" case.
        if (envHost !== window.location.host) {
            return window.location.origin;
        }
    } catch {
        return window.location.origin;
    }
    return fromEnv;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// All API calls include cookies for session.
// Iter49b — every request has a 60s hard deadline so a stalled socket
// (mobile radio flip, backend restart, slow query) can't hang the UI
// forever. Endpoints that legitimately take longer (LLM JD/chat/PDF gen)
// override this per-call via `{ timeout: 120000 }` on the axios request.
export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    timeout: 60000,
});

export function formatApiError(err) {
    // Iter 55.1b — delegate to the unified apiErrors helper so every
    // caller in the codebase (there are hundreds) gets the upgraded
    // "message · next_step" line automatically. Callers wanting a
    // richer toast should import { apiErrorToast } from "@/lib/apiErrors"
    // and pass the sonner `toast` object.
    return _formatApiErrorUnified(err);
}

// Iter 55.1b — global axios response interceptor: attach a normalized
// error shape to every rejection so callers that don't yet use the new
// helper still get consistent info via `err._userError`. Never swallow
// the rejection.
api.interceptors.response.use(
    (r) => r,
    (err) => {
        try {
            err._userError = _normalizeApiError(err);
        } catch {
            // Interceptor must never itself throw — swallow normalize failures.
        }
        return Promise.reject(err);
    },
);

// ---------- Auth ----------
export const authApi = {
    register: (payload) => api.post("/auth/register", payload).then((r) => r.data),
    verifyOtp: (email, code) => api.post("/auth/verify-otp", { email, code }).then((r) => r.data),
    resendOtp: (email) => api.post("/auth/resend-otp", { email }).then((r) => r.data),
    login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
    logout: () => api.post("/auth/logout").then((r) => r.data),
    me: () => api.get("/auth/me").then((r) => r.data),
    forgot: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
    reset: (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
    googleCallback: (session_id) => api.post("/auth/google/callback", { session_id }).then((r) => r.data),
    completeProfile: (payload) => api.post("/auth/complete-profile", payload).then((r) => r.data),
    selectPlan: (plan) => api.post("/auth/plans/select", { plan }).then((r) => r.data),
    // Phase 3a — OTP-only 3-step employer flow
    registerEmployer: (payload) => api.post("/auth/register-employer", payload).then((r) => r.data),
    verifyRegisterOtp: (work_email, code) => api.post("/auth/verify-register-otp", { work_email, code }).then((r) => r.data),
    employerLoginOtp: (work_email) => api.post("/auth/employer-login-otp", { work_email }).then((r) => r.data),
    verifyLoginOtp: (work_email, code) => api.post("/auth/verify-login-otp", { work_email, code }).then((r) => r.data),
    quickProfile: (payload) => api.post("/employer/quick-profile", payload).then((r) => r.data),
    setPassword: (new_password) => api.post("/employer/set-password", { new_password }).then((r) => r.data),
    requestCompanyAccess: (payload) => api.post("/auth/request-company-access", payload).then((r) => r.data),
    acceptAgreement: (version = "1.0") => api.post("/employer/accept-agreement", { version }).then((r) => r.data),
    pendingJoinRequests: () => api.get("/employer/pending-join-requests").then((r) => r.data),
    approveJoinRequest: (reqId) => api.post(`/employer/join-requests/${reqId}/approve`).then((r) => r.data),
    declineJoinRequest: (reqId) => api.post(`/employer/join-requests/${reqId}/decline`).then((r) => r.data),
    permissionSchema: () => api.get("/team/permissions/schema").then((r) => r.data),
    updateTeamPermissions: (memberId, permissions) => api.patch(`/team/${memberId}/permissions`, { permissions }).then((r) => r.data),
};

export const employerAddressApi = {
    list: () => api.get("/employer/profile/addresses").then((r) => r.data),
    add: (payload) => api.post("/employer/profile/addresses", payload).then((r) => r.data),
    edit: (id, payload) => api.patch(`/employer/profile/addresses/${id}`, payload).then((r) => r.data),
    remove: (id) => api.delete(`/employer/profile/addresses/${id}`).then((r) => r.data),
    addContact: (addrId, payload) => api.post(`/employer/profile/addresses/${addrId}/contacts`, payload).then((r) => r.data),
    editContact: (addrId, cid, payload) => api.patch(`/employer/profile/addresses/${addrId}/contacts/${cid}`, payload).then((r) => r.data),
    removeContact: (addrId, cid) => api.delete(`/employer/profile/addresses/${addrId}/contacts/${cid}`).then((r) => r.data),
};

// ---------- Candidate personality assessment ----------
export const personalityApi = {
    questions: () => api.get("/candidate/personality-questions").then((r) => r.data),
    submit: (answers) => api.post("/candidate/personality-assessment", { answers }).then((r) => r.data),
    report: () => api.get("/candidate/personality-report").then((r) => r.data),
};

// ---------- Iter52a: Billing catalog (public read) ----------
export const billingCatalogApi = {
    unitRates: () => api.get("/billing/unit-rates").then((r) => r.data),
    // Iter52b — active plans list (used by CreditsTab assign-plan dropdown).
    listPlans: () => api.get("/bmi-ops/billing/plans?active_only=true").then((r) => r.data),
};

// ---------- Iter52c-followup: Ops observability (Pilot Trust Watch) ----------
export const opsObservabilityApi = {
    failedJobs: (params = {}) => api.get("/bmi-ops/observability/failed-jobs", { params }).then((r) => r.data),
    latency: (params = {}) => api.get("/bmi-ops/observability/latency", { params }).then((r) => r.data),
};

// ---------- Job boost (customer price from billing_units.BOOST_INT) ----------
export const boostApi = {
    initiate: (jobId) => api.post(`/jobs/${jobId}/boost/initiate`).then((r) => r.data),
    confirm: (jobId, payload) => api.post(`/jobs/${jobId}/boost/confirm`, payload).then((r) => r.data),
};

// ---------- GSTIN lookup (public — no auth) ----------
export const gstinApi = {
    lookup: (gstin) => api.post("/gstin/lookup", { gstin }).then((r) => r.data),
};

// ---------- Consultant Chat (protected) ----------
export const chatApi = {
    createSession: () => api.post("/sessions").then((r) => r.data),
    listSessions: () => api.get("/sessions").then((r) => r.data),
    getSession: (id) => api.get(`/sessions/${id}`).then((r) => r.data),
    deleteSession: (id) => api.delete(`/sessions/${id}`).then((r) => r.data),
    send: (session_id, message) => api.post("/chat/send", { session_id, message }, { timeout: 180000 }).then((r) => r.data),
    upload: (session_id, file) => {
        const form = new FormData();
        form.append("file", file);
        return api
            .post(`/chat/upload?session_id=${encodeURIComponent(session_id)}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000,
            })
            .then((r) => r.data);
    },
    generateJD: (session_id) => api.post("/jd/generate", { session_id }, { timeout: 30000 }).then((r) => r.data),
    // Iter52c — recovery / IMMEDIATE refetch after a network hiccup.
    // Returns the current JD for the session if one has been saved, or
    // throws 404 if nothing landed yet. Kept as a distinct method so
    // callers can differentiate between "kick generation" and "check
    // if it already ran".
    getJd: (session_id) => api.get(`/jd/${session_id}`, { timeout: 15000 }).then((r) => r.data),
    downloadUrl: (session_id, format) => `${API}/jd/${session_id}/download/${format}`,
    /**
     * Iter48 — Download JD as PDF/DOCX using axios so cookies + auth headers
     * ride the same-origin path that already works everywhere else in the
     * app. `window.open(url)` was fragile in production: any non-200 opened
     * a blank tab with no toast, and Content-Disposition with special chars
     * in the role title (`/`, `?`, `&`, non-ASCII) silently broke downloads
     * behind CDN proxies. This variant surfaces errors as toasts and saves
     * via a Blob URL — same UX users expect.
     */
    downloadJd: async (session_id, format) => {
        const res = await api.get(`/jd/${session_id}/download/${format}`, {
            responseType: "blob",
        });
        // Pull a filename from Content-Disposition if the server provides one
        // (RFC 5987 `filename*=UTF-8''...` preferred, fall back to `filename=`).
        const cdisp = res.headers?.["content-disposition"] || "";
        let name = `job-description.${format}`;
        const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cdisp);
        const ascii = /filename\s*=\s*"?([^";]+)"?/i.exec(cdisp);
        if (utf8?.[1]) { try { name = decodeURIComponent(utf8[1]); } catch { /* keep default */ } }
        else if (ascii?.[1]) name = ascii[1];
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        return { ok: true, filename: name };
    },
};

// ---------- Jobs ----------
export const jobsApi = {
    saveFromSession: (session_id) => api.post("/jobs/from-session", { session_id }).then((r) => r.data),
    list: () => api.get("/jobs").then((r) => r.data),
    get: (id) => api.get(`/jobs/${id}`).then((r) => r.data),
    update: (id, payload) => api.patch(`/jobs/${id}`, payload).then((r) => r.data),
    addSlots: (id, slots) => api.post(`/jobs/${id}/slots`, { slots }).then((r) => r.data),
    deleteSlot: (jobId, slotId) => api.delete(`/jobs/${jobId}/slots/${slotId}`).then((r) => r.data),
    publish: (id) => api.post(`/jobs/${id}/publish`).then((r) => r.data),
    delete: (id) => api.delete(`/jobs/${id}`).then((r) => r.data),
    // Item 4 (2026-02-28) — clone JD + reqs + screening + weights + OCEAN into a new draft.
    duplicate: (id) => api.post(`/jobs/${id}/duplicate`).then((r) => r.data),
    // Iter55.2-EmpSprint1 — inspect + safely clean unbooked invalid slots.
    invalidSlots: (id) => api.get(`/jobs/${id}/invalid-slots`).then((r) => r.data),
    cleanupInvalidSlots: (id) => api.post(`/jobs/${id}/cleanup-invalid-slots`).then((r) => r.data),
    // Iter55.2 Phase 2 — lifecycle actions + bulk slots.
    deletePreflight: (id) => api.get(`/jobs/${id}/delete-preflight`).then((r) => r.data),
    bulkSlots: (id, payload) => api.post(`/jobs/${id}/bulk-slots`, payload).then((r) => r.data),
    // Phase B — structured requirements.
    updateRequirements: (id, payload) =>
        api.patch(`/jobs/${id}/requirements`, payload).then((r) => r.data),
    editScreeningPlan: (id, payload) =>
        api.patch(`/screening/plan/${id}`, payload).then((r) => r.data),
    dashboard: () => api.get("/dashboard/stats").then((r) => r.data),
    publicList: () => api.get("/public/jobs").then((r) => r.data),
    publicGet: (slug) => api.get(`/public/jobs/${slug}`).then((r) => r.data),
    // Phase C1 — public candidate self-check (mandatory-only).
    publicSelfCheck: (slug, payload) =>
        api.post(`/public/jobs/${slug}/self-check`, payload).then((r) => r.data),
};

// Legacy export retained for backward compat
export const downloadJDUrl = chatApi.downloadUrl;

// ---------- Applications (public candidate flow) ----------

function candidateAuthConfig(token) {
    return token ? { headers: { "X-Candidate-Token": token } } : {};
}

// Batch A1 (2026-03-02) — visitor↔candidate identity join capture.
//
// Best-effort read of PostHog's distinct_id + $device_id at apply-submit
// time. The values ride the create-application POST and are persisted
// onto the application row so ANONYMOUS PostHog behaviour (ad-click →
// landing → apply) can later be joined to REAL pipeline outcomes
// (screening / hire / rejection) in a Stage 2 lookup.
//
// PII / privacy discipline:
//   * Both IDs are opaque pseudonymous handles (no email / phone / name).
//   * NEVER add PII (email, phone, name, application_id) to any
//     PostHog event as part of this batch.
//   * NEVER throw — if posthog is blocked / not loaded / mid-init, we
//     send nulls and the application submits normally. Capturing this
//     link must NEVER delay or break the apply flow.
function readPosthogIds() {
    try {
        if (typeof window === "undefined") return { ph_distinct_id: null, ph_device_id: null };
        const ph = window.posthog;
        if (!ph) return { ph_distinct_id: null, ph_device_id: null };
        let distinct = null, device = null;
        try {
            if (typeof ph.get_distinct_id === "function") distinct = ph.get_distinct_id();
        } catch { /* ph broken or not initialised */ }
        try {
            if (typeof ph.get_property === "function") device = ph.get_property("$device_id");
        } catch { /* $device_id can be undefined pre-first-event */ }
        // Sanitize + cap. String-only; anything else → null.
        const safe = (v) => (typeof v === "string" && v.length && v.length <= 120) ? v : null;
        return { ph_distinct_id: safe(distinct), ph_device_id: safe(device) };
    } catch {
        return { ph_distinct_id: null, ph_device_id: null };
    }
}

export const applicationsApi = {
    start: (payload) => {
        // Iteration 43c — auto-inject UTM attribution if captured this session.
        // Explicit values on `payload` win over localStorage (caller override).
        const utm = readUtm();
        // Batch A1 — best-effort PostHog identity capture. Read is
        // synchronous + guarded; never blocks or throws upward.
        const ph = readPosthogIds();
        const merged = { ...(utm || {}), ...ph, ...payload };
        return api.post("/public/applications/start", merged)
            .then((r) => r.data);
    },
    get: (appId, token) => api.get(`/public/applications/${appId}`, candidateAuthConfig(token)).then((r) => r.data),
    uploadCv: (appId, token, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.post(`/public/applications/${appId}/cv`, form, {
            headers: { "Content-Type": "multipart/form-data", "X-Candidate-Token": token },
        }).then((r) => r.data);
    },
    // Phase C1 — paste-text / LinkedIn fallback path.
    uploadCvText: (appId, token, text, source = "paste", displayName = null) =>
        api.post(
            `/public/applications/${appId}/cv-text`,
            { text, source, display_name: displayName },
            candidateAuthConfig(token),
        ).then((r) => r.data),
    confirmCv: (appId, token, parsed) =>
        api.post(
            `/public/applications/${appId}/cv-confirm`,
            { parsed },
            candidateAuthConfig(token),
        ).then((r) => r.data),
    consent: (appId, token, accept, dpdp = null) => api.post(`/public/applications/${appId}/consent`, { accept, dpdp_consent: dpdp }, candidateAuthConfig(token)).then((r) => r.data),
    startScreening: (appId, token) => api.post(`/public/applications/${appId}/start-screening`, {}, candidateAuthConfig(token)).then((r) => r.data),
    submitMcq: (appId, token, questionId, chosenIndex) =>
        api.post(`/public/applications/${appId}/mcq-answer`, { question_id: questionId, chosen_index: chosenIndex }, candidateAuthConfig(token)).then((r) => r.data),
    // Iter87 Audit — legacy `submitAv` (POST /public/applications/{id}/av-answer)
    // deleted. Modular flow uses the /public/av-task/{token}/chunk* pipeline
    // (see avTaskApi below), which schedules LLM AV scoring async.
    complete: (appId, token) => api.post(`/public/applications/${appId}/complete`, {}, { ...candidateAuthConfig(token), timeout: 30000 }).then((r) => r.data),
    // Iter52c — polling companion for the async scoring job. Same auth.
    completeStatus: (appId, token) => api.get(`/public/applications/${appId}/complete-status`, candidateAuthConfig(token)).then((r) => r.data),
    skipVideo: (appId, token) => api.post(`/public/applications/${appId}/skip-video`, {}, candidateAuthConfig(token)).then((r) => r.data),
    availableSlots: (appId, token) => api.get(`/public/applications/${appId}/available-slots`, candidateAuthConfig(token)).then((r) => r.data),
    bookSlot: (appId, token, slotId) => api.post(`/public/applications/${appId}/book-slot`, { slot_id: slotId }, candidateAuthConfig(token)).then((r) => r.data),
    // Iter56 Candidate Sprint 1 · Phase 2 — self-serve cancel + reschedule.
    // Cutoff: 4h before slot. Max 2 reschedules. Cancel refunds employer
    // wallet ONLY when >=4h AND a prior debit exists (idempotent on backend).
    cancelInterview: (appId, token, reason = "") => api.post(`/public/applications/${appId}/cancel`, { reason }, candidateAuthConfig(token)).then((r) => r.data),
    rescheduleInterview: (appId, token, slotId) => api.post(`/public/applications/${appId}/reschedule`, { slot_id: slotId }, candidateAuthConfig(token)).then((r) => r.data),
    // Employer view
    employerList: (jobId) => api.get(`/applications${jobId ? `?job_id=${jobId}` : ""}`).then((r) => r.data),
    jobPipeline: (jobId) => api.get(`/jobs/${jobId}/pipeline`).then((r) => r.data),
    report: (appId) => api.get(`/applications/${appId}/report`).then((r) => r.data),
};

// ---------- Reporting API tokens (2026-06, SUPER_ADMIN) ----------
export const reportingTokensApi = {
    list: () => api.get("/bmi-ops/reporting-tokens").then((r) => r.data),
    create: (payload) => api.post("/bmi-ops/reporting-tokens", payload).then((r) => r.data),
    revoke: (id) => api.post(`/bmi-ops/reporting-tokens/${id}/revoke`).then((r) => r.data),
};

// ---------- Admin ----------
export const adminApi = {
    stats: () => api.get("/admin/stats").then((r) => r.data),
    listEmployers: (q, opts = {}) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (opts.include_inactive) params.set("include_inactive", "true");
        const qs = params.toString();
        return api.get(`/admin/employers${qs ? `?${qs}` : ""}`).then((r) => r.data);
    },
    updateEmployer: (userId, payload) => api.patch(`/admin/employers/${userId}`, payload).then((r) => r.data),
    listJobs: (status) => api.get(`/admin/jobs${status ? `?status=${status}` : ""}`).then((r) => r.data),
    listCandidates: (q) => api.get(`/admin/candidates${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => r.data),
    updateCandidate: (cid, payload) => api.patch(`/admin/candidates/${cid}`, payload).then((r) => r.data),
    deleteCandidate: (cid) => api.delete(`/admin/candidates/${cid}`).then((r) => r.data),
    activity: (limit = 50) => api.get(`/admin/activity?limit=${limit}`).then((r) => r.data),
    subscriptions: () => api.get("/admin/subscriptions").then((r) => r.data),
    getEmployerDetail: (userId) => api.get(`/admin/employers/${userId}`).then((r) => r.data),
    listApplications: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
        ).toString();
        return api.get(`/admin/applications${qs ? `?${qs}` : ""}`).then((r) => r.data);
    },
    // Iter72 (2026-02-24) — AV unplayable alerts feed.
    avUnplayableAlerts: (status = "unresolved", limit = 200) => {
        const qs = new URLSearchParams();
        if (status) qs.set("status", status);
        if (limit) qs.set("limit", String(limit));
        return api.get(`/admin/av-unplayable-alerts?${qs.toString()}`).then((r) => r.data);
    },
    resolveAvUnplayableAlert: (applicationId, questionId) =>
        api.post(`/admin/av-unplayable-alerts/${applicationId}/${questionId}/resolve`).then((r) => r.data),
    // Iter73b (2026-02-25) — CV booking-gate mode.
    cvBookingGateState: () =>
        api.get("/admin/cv-booking-gate/state").then((r) => r.data),
    cvBookingGateSetMode: (mode) =>
        api.post("/admin/cv-booking-gate/mode", { mode }).then((r) => r.data),
};

// ---------- Internal ops (Super admin / RBAC) ----------
export const opsApi = {
    init: (email) => api.post("/admin/auth/init", { email }).then((r) => r.data),
    verifyOtp: (email, otp) => api.post("/admin/auth/verify-otp", { email, otp }).then((r) => r.data),
    logout: () => api.post("/admin/auth/logout").then((r) => r.data),
    me: () => api.get("/admin/auth/me").then((r) => r.data),
    overview: () => api.get("/admin/overview").then((r) => r.data),
    costSummary: (month) => api.get(`/admin/cost-summary${month ? `?month=${month}` : ""}`).then((r) => r.data),
    costDetail: (employerId, month) => api.get(`/admin/cost-detail/${employerId}${month ? `?month=${month}` : ""}`).then((r) => r.data),
    listTeam: () => api.get("/admin/team").then((r) => r.data),
    addTeam: (payload) => api.post("/admin/team", payload).then((r) => r.data),
    patchTeam: (id, payload) => api.patch(`/admin/team/${id}`, payload).then((r) => r.data),
    uploadEmployerGst: (employer_user_id, file) => {
        const fd = new FormData(); fd.append("gst_file", file);
        return api.post(`/admin/employer/${employer_user_id}/gst-certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    auditLog: (limit = 200) => api.get(`/admin/audit-log?limit=${limit}`).then((r) => r.data),
    hardDelete: (entity_type, id) => api.post("/admin/hard-delete", { entity_type, id }).then((r) => r.data),
    bulkPreview: (entity_type, email_pattern) => api.post("/admin/bulk-delete/preview", { entity_type, email_pattern }).then((r) => r.data),
    bulkDelete: (entity_type, email_pattern) => api.post("/admin/bulk-delete", { entity_type, email_pattern }).then((r) => r.data),
    edit: (entity_type, id, fields) => api.patch("/admin/edit", { entity_type, id, fields }).then((r) => r.data),
    impersonate: (entity_type, id) => api.post("/admin/impersonate-token", { entity_type, id }).then((r) => r.data),
    exportUrl: (type, from_, to) => {
        const params = new URLSearchParams({ type });
        if (from_) params.append("from", from_);
        if (to) params.append("to", to);
        return `${API}/admin/export?${params.toString()}`;
    },
    onboardingDashboard: () => api.get("/admin/onboarding-dashboard").then((r) => r.data),
    activateEmployer: (id) => api.post(`/admin/employer/${id}/activate`).then((r) => r.data),
    suspendEmployer: (id, reason) => api.post(`/admin/employer/${id}/suspend`, { reason }).then((r) => r.data),
    markEmployerVerified: (id, reason) => api.post(`/admin/employer/${id}/mark-verified`, { reason }).then((r) => r.data),
    accountAdminCandidates: (id) => api.get(`/admin/employer/${id}/account-admin-candidates`).then((r) => r.data),
    setAccountAdmin: (id, payload) => api.post(`/admin/employer/${id}/set-account-admin`, payload).then((r) => r.data),
    reactivateEmployer: (id) => api.post(`/admin/employer/${id}/reactivate`).then((r) => r.data),
    nudgeEmployer: (user_id) => api.post("/admin/nudge", { user_type: "employer", user_id, stage: "pending_verification" }).then((r) => r.data),
    nudgeCandidate: (user_id, stage) => api.post("/admin/nudge", { user_type: "candidate", user_id, stage }).then((r) => r.data),
    // P0 outage fix (2026-02-20) — /bmi-ops UI errors tab + legacy jobs summary.
    uiErrors: ({ limit = 100, group } = {}) => {
        const q = new URLSearchParams({ limit: String(limit) });
        if (group) q.set("group", group);
        return api.get(`/ops/ui-errors?${q.toString()}`).then((r) => r.data);
    },
    legacyJobsSummary: () => api.get("/ops/legacy-jobs-summary").then((r) => r.data),
    unscopedHms: () => api.get("/ops/unscoped-hms").then((r) => r.data),
    // Wave-2 notification rollout guard.
    notificationFlags: () => api.get("/ops/notification-flags").then((r) => r.data),
    toggleNotificationFlag: (trigger, enabled) => api.post(`/ops/notification-flags/${trigger}`, { enabled }).then((r) => r.data),
    emailBounceHistory: (hours = 72) => api.get(`/admin/email-health/history?hours=${hours}`).then((r) => r.data),
    // Cluster D pt2 (2026-02-25) — Test-noise bounce-event cleanup.
    // Cluster D pt3 (2026-02-25) — Preview + apply accept manual_addresses[]
    // so ops can hand-pick real-looking bounces they've triaged as QA.
    noiseCleanupPreview: (days = 7, manualAddresses = []) =>
        api.post(`/admin/email-health/noise-cleanup/preview`, { days, manual_addresses: manualAddresses }).then((r) => r.data),
    noiseCleanupApply: (days = 7, manualAddresses = []) =>
        api.post(`/admin/email-health/noise-cleanup/apply`, { days, manual_addresses: manualAddresses }).then((r) => r.data),
    // Iter61 Phase 2 — Journey Live board.
    journeyBoard: (params = {}) => {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") q.append(k, v);
        });
        const qs = q.toString();
        return api.get(`/ops/journey-board${qs ? `?${qs}` : ""}`).then((r) => r.data);
    },
    journeyState: (entityType, entityId) =>
        api.get(`/ops/journey-state/${entityType}/${entityId}`).then((r) => r.data),
    journeyVocab: () => api.get("/ops/journey-state/vocab").then((r) => r.data),
    journeyClusters: (window_hours = 24, min_count = 3) =>
        api.get(`/ops/journey-clusters?window_hours=${window_hours}&min_count=${min_count}`).then((r) => r.data),
    journeySystemHealth: () => api.get("/ops/system-health").then((r) => r.data),
    journeySourceFunnel: (days) =>
        api.get("/ops/source-funnel", { params: days ? { days } : {} }).then((r) => r.data),
    // Iteration 44 — Attention strip (Overview tab)
    attention: () => api.get("/admin/attention").then((r) => r.data),
    // Iter96 (2026-02-27) — Employer manual-review approvals.
    listEmployerApprovals: (status = "pending", limit = 200) =>
        api.get(`/admin/employer-approvals?status=${status}&limit=${limit}`).then((r) => r.data),
    decideEmployerApproval: (user_id, decision, reason) =>
        api.post("/admin/employer-approvals/decide", { user_id, decision, reason: reason || null }).then((r) => r.data),
};

// ---------- Auditor (moved into /bmi-ops via Iteration 44) ----------
export const auditQueueApi = {
    stats: () => api.get("/auditor/stats").then((r) => r.data),
    pending: () => api.get("/auditor/pending").then((r) => r.data),
    reviewed: () => api.get("/auditor/reviewed").then((r) => r.data),
    approve: (app_id, note) => api.post(`/auditor/${app_id}/approve`, { note }).then((r) => r.data),
    reject: (app_id, reason) => api.post(`/auditor/${app_id}/reject`, { reason }).then((r) => r.data),
};

// ---------- Employer usage summary ----------
export const usageApi = {
    summary: () => api.get("/employer/usage-summary").then((r) => r.data),
};

// ---------- Jobs — campaigns + funnel + media spend (Iteration 43c/d/e) ----------
export const jobInsightsApi = {
    campaigns: (jobId) => api.get(`/jobs/${jobId}/campaigns`).then((r) => r.data),
    funnel: (jobId) => api.get(`/jobs/${jobId}/funnel`).then((r) => r.data),
    funnelCsvUrl: (jobId) => `${API}/jobs/${jobId}/funnel.csv`,
    listSpend: (jobId) => api.get(`/jobs/${jobId}/media-spend`).then((r) => r.data),
    addSpend: (jobId, entry) => api.post(`/jobs/${jobId}/media-spend`, entry).then((r) => r.data),
    deleteSpend: (jobId, spendId) => api.delete(`/jobs/${jobId}/media-spend/${spendId}`).then((r) => r.data),
};

// ---------- Ops pilot strip (Iteration 43e) ----------
export const opsPilotApi = {
    pilotStrip: () => api.get("/admin/pilot-strip").then((r) => r.data),
};

// ---------- Billing ----------
export const billingApi = {
    status: () => api.get("/billing/status").then((r) => r.data),
    subscribe: (plan) => api.post("/billing/subscribe", { plan }).then((r) => r.data),
    cancel: () => api.post("/billing/cancel").then((r) => r.data),
};

// ---------- Billing v3 (Batch A — prepaid plan model, flag-gated) ----------
export const billingV3Api = {
    status: () => api.get("/billing-v3/status").then((r) => r.data),
    wallet: () => api.get("/billing-v3/wallet").then((r) => r.data),
    plans: () => api.get("/billing-v3/plans").then((r) => r.data),
    rechargeOffers: () => api.get("/billing-v3/recharge-offers").then((r) => r.data),
    gstRecords: () => api.get("/billing-v3/gst-records").then((r) => r.data),
    invoices: () => api.get("/billing-v3/invoices").then((r) => r.data),
    invoicePdfUrl: (invoiceNo) => `${process.env.REACT_APP_BACKEND_URL}/api/billing-v3/invoices/${invoiceNo.replace(/^BMI\//, "")}/pdf`,
    locked: () => api.get("/billing-v3/locked").then((r) => r.data),
    quote: (payload) => api.post("/billing-v3/quote", payload).then((r) => r.data),
    planPurchaseInitiate: (plan_code) =>
        api.post("/billing-v3/plan/purchase-initiate", { plan_code }).then((r) => r.data),
    planPurchaseConfirm: (payload) =>
        api.post("/billing-v3/plan/purchase-confirm", payload).then((r) => r.data),
    activateTrial: () => api.post("/billing-v3/plan/activate-trial", {}).then((r) => r.data),
    enterpriseEnquiry: (payload) => api.post("/billing-v3/enterprise-enquiry", payload).then((r) => r.data),
    rechargeInitiate: (amount_inr) =>
        api.post("/billing-v3/recharge-initiate", { amount_inr }).then((r) => r.data),
    rechargeConfirm: (payload) =>
        api.post("/billing-v3/recharge-confirm", payload).then((r) => r.data),
    // ops (SUPER_ADMIN)
    opsMigrationDryRun: () => api.get("/bmi-ops/billing-v3/migration/dry-run").then((r) => r.data),
    opsEmployerBilling: (employer_id) =>
        api.get(`/bmi-ops/billing-v3/employer/${employer_id}/billing`).then((r) => r.data),
    opsLockedWithoutPayment: () =>
        api.get("/bmi-ops/billing-v3/locked-without-payment").then((r) => r.data),
    opsGrantTrialAgain: (employer_id, reason) =>
        api.post("/bmi-ops/billing-v3/grant-trial-again", { employer_id, reason }).then((r) => r.data),
    // custom plan (employer-facing)
    myCustomPlan: () => api.get("/billing-v3/my-custom-plan").then((r) => r.data),
    // ops — vouchers
    opsVoucherCreate: (payload) => api.post("/bmi-ops/billing-v3/vouchers", payload).then((r) => r.data),
    opsVoucherList: (employer_id) => api.get("/bmi-ops/billing-v3/vouchers" + (employer_id ? `?employer_id=${employer_id}` : "")).then((r) => r.data),
    opsVoucherReport: () => api.get("/bmi-ops/billing-v3/vouchers/report").then((r) => r.data),
    opsVoucherApprove: (id) => api.post(`/bmi-ops/billing-v3/vouchers/${id}/approve`, {}).then((r) => r.data),
    opsVoucherReject: (id, reason) => api.post(`/bmi-ops/billing-v3/vouchers/${id}/reject`, { reason }).then((r) => r.data),
    opsVoucherCancel: (id, reason) => api.post(`/bmi-ops/billing-v3/vouchers/${id}/cancel`, { reason }).then((r) => r.data),
    // ops — custom plans + offline payments
    opsCustomPlanCreate: (payload) => api.post("/bmi-ops/billing-v3/custom-plans", payload).then((r) => r.data),
    opsCustomPlanList: () => api.get("/bmi-ops/billing-v3/custom-plans").then((r) => r.data),
    opsCustomPlanReport: () => api.get("/bmi-ops/billing-v3/custom-plans/report").then((r) => r.data),
    opsCustomPlanApprove: (code) => api.post(`/bmi-ops/billing-v3/custom-plans/${code}/approve`, {}).then((r) => r.data),
    opsCustomPlanReject: (code, reason) => api.post(`/bmi-ops/billing-v3/custom-plans/${code}/reject`, { reason }).then((r) => r.data),
    opsOfflineRecord: (payload) => api.post("/bmi-ops/billing-v3/offline-payments", payload).then((r) => r.data),
    opsOfflineQuote: (params) => api.get("/bmi-ops/billing-v3/offline-payments/quote", { params }).then((r) => r.data),
    opsOfflineList: () => api.get("/bmi-ops/billing-v3/offline-payments").then((r) => r.data),
    opsOfflineApprove: (id) => api.post(`/bmi-ops/billing-v3/offline-payments/${id}/approve`, {}).then((r) => r.data),
    // role-aware helpers
    opsMyAccounts: () => api.get("/bmi-ops/billing-v3/my-accounts").then((r) => r.data),
    opsEmployerSearch: (q) => api.get(`/bmi-ops/billing-v3/employers/search?q=${encodeURIComponent(q)}`).then((r) => r.data),
    opsApprovalsPending: () => api.get("/bmi-ops/billing-v3/approvals/pending").then((r) => r.data),
    opsOfflineAttachment: (file) => {
        const form = new FormData();
        form.append("file", file);
        return api.post("/bmi-ops/billing-v3/offline-attachment", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
};

// ---------- Wallet (Iteration 43b — Prepaid + PAYG metering) ----------
export const walletApi = {
    get: () => api.get("/wallet").then((r) => r.data),
    initiateTopup: (amount_inr) => api.post("/wallet/topup/initiate", { amount_inr }).then((r) => r.data),
    confirmTopup: (payload) => api.post("/wallet/topup/confirm", payload).then((r) => r.data),
};

// ---------- Ops wallet (SUPER_ADMIN — rate card + adjustments) ----------
export const opsWalletApi = {
    getRates: () => api.get("/bmi-ops/wallet/rates").then((r) => r.data),
    setRates: (payload) => api.post("/bmi-ops/wallet/rates", payload).then((r) => r.data),
    rateAudit: (limit = 100) => api.get(`/bmi-ops/wallet/rate-audit?limit=${limit}`).then((r) => r.data),
    getEmployerWallet: (employer_id) => api.get(`/bmi-ops/wallet/${employer_id}`).then((r) => r.data),
    adjust: (employer_id, payload) => api.post(`/bmi-ops/wallet/${employer_id}/adjust`, payload).then((r) => r.data),
};

// ---------- Ops employer actions (SUPER_ADMIN — Iter48 Option B) ----------
export const opsEmployerActionsApi = {
    tagInternal: (employer_id, reason) =>
        api.post(`/bmi-ops/employers/${employer_id}/tag-internal`, { reason }).then((r) => r.data),
    restoreActive: (employer_id, reason) =>
        api.post(`/bmi-ops/employers/${employer_id}/restore-active`, { reason }).then((r) => r.data),
    mergePreview: (employer_id, target_email, role = "HIRING_MANAGER") =>
        api.post(`/bmi-ops/employers/${employer_id}/merge/preview`,
                 { target_email, role }).then((r) => r.data),
    mergeExecute: (employer_id, { target_email, role = "HIRING_MANAGER", confirmation, reason }) =>
        api.post(`/bmi-ops/employers/${employer_id}/merge/execute`,
                 { target_email, role, confirmation, reason }).then((r) => r.data),
};

// ---------- Ops credits (SUPER_ADMIN — Iter48 credit-limit mgmt) ----------
export const opsCreditsApi = {
    defaults: () => api.get("/bmi-ops/credits/defaults").then((r) => r.data),
    overview: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
        ).toString();
        return api.get(`/bmi-ops/credits/overview${qs ? `?${qs}` : ""}`).then((r) => r.data);
    },
    get: (employer_id) => api.get(`/bmi-ops/credits/${employer_id}`).then((r) => r.data),
    setPlan: (employer_id, payload) => api.post(`/bmi-ops/credits/${employer_id}/plan`, payload).then((r) => r.data),
    // Iter52b — v2 plan_code pointer (does NOT modify legacy `plan` string).
    setPlanCode: (employer_id, payload) => api.post(`/bmi-ops/credits/${employer_id}/plan-code`, payload).then((r) => r.data),
    setOverride: (employer_id, payload) => api.post(`/bmi-ops/credits/${employer_id}/override`, payload).then((r) => r.data),
    resetOverride: (employer_id, payload) => api.post(`/bmi-ops/credits/${employer_id}/reset`, payload).then((r) => r.data),
    // Iter49c — job-post count override
    setJobLimitOverride: (employer_id, payload) => api.post(`/bmi-ops/credits/${employer_id}/job-limit-override`, payload).then((r) => r.data),
    resetJobLimitOverride: (employer_id, payload) => api.post(`/bmi-ops/credits/${employer_id}/reset-job-limit-override`, payload).then((r) => r.data),
};

// ---------- Employer Profile (brand kit) ----------
export const employerProfileApi = {
    get: () => api.get("/employer/profile").then((r) => r.data),
    update: (payload) => api.patch("/employer/profile", payload).then((r) => r.data),
    uploadLogo: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/employer/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    deleteLogo: () => api.delete("/employer/logo").then((r) => r.data),
    // Batch 12 (2026-03-01) — probe employer's website for a logo (apple-touch-icon,
    // manifest icons, og:image, favicon, or Google s2 fallback) and persist the
    // best match. Backend refuses to overwrite an existing logo unless
    // overwrite=true is passed.
    fetchLogoFromWebsite: ({ website_url = null, overwrite = false } = {}) =>
        api.post("/employer/logo/fetch-from-website", { website_url, overwrite }).then((r) => r.data),
    uploadBrochure: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/employer/brochure", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    deleteBrochure: () => api.delete("/employer/brochure").then((r) => r.data),
};

// ---------- Signed download URLs ----------
export const uploadsApi = {
    sign: (path, expiresInHours = 24) =>
        api.post("/uploads/sign", { path, expires_in_hours: expiresInHours }).then((r) => r.data),
};

// ---------- Phase 3b-2: Employer pipeline + candidate drawer ----------
export const pipelineApi = {
    get: (jobId) => api.get(`/pipeline/${jobId}`).then((r) => r.data),
    application: (appId) => api.get(`/pipeline/application/${appId}`).then((r) => r.data),
    updateStatus: (appId, status, reason = null) =>
        api.patch(`/applications/${appId}/status`, { status, reason }).then((r) => r.data),
    avSummary: (appId, questionId) =>
        api.post(`/applications/${appId}/av-summary`, { question_id: questionId }).then((r) => r.data),
    addNote: (appId, text) =>
        api.post(`/applications/${appId}/notes`, { text }).then((r) => r.data),
    // AV playback URL (auth-checked byte stream)
    avPlaybackUrl: (appId, questionId) => `${API}/applications/${appId}/av/${questionId}`,
    // Phase 3b-3
    interviewers: (jobId) => api.get(`/team/interviewers/${jobId}`).then((r) => r.data),
    scheduleInterview: (appId, payload) =>
        api.post(`/applications/${appId}/schedule-interview`, payload).then((r) => r.data),
    markNoShow: (appId, offer_reschedule) =>
        api.patch(`/applications/${appId}/mark-no-show`, { offer_reschedule }).then((r) => r.data),
    markInterviewed: (appId) =>
        api.patch(`/applications/${appId}/mark-interviewed`).then((r) => r.data),
    // Iter72 (2026-02-24) — Shortlist / Unshortlist verbs.
    shortlist: (appId) =>
        api.post(`/applications/${appId}/shortlist`).then((r) => r.data),
    unshortlist: (appId) =>
        api.post(`/applications/${appId}/unshortlist`).then((r) => r.data),
    // Iter87 Batch C — employer vetoes a reserve auto-offer during the 12h
    // pending_veto window. Rolls the offer + auto-offers the next candidate.
    reserveVeto: (appId, reason) =>
        api.post(`/applications/${appId}/reserve-veto`, { reason: reason || "" }).then((r) => r.data),
    // Iter87 EmpBatch Part A — quick-hire from the drawer.
    // Distinct from scorecardApi.finalDecision(hired) — this endpoint
    // doesn't require submitted scorecards and does NOT fire the hire
    // email to the candidate. Stores recommendation + note for audit.
    employerHire: (appId, { recommendation = null, note = "" } = {}) =>
        api.post(`/applications/${appId}/employer-hire`, { recommendation, note }).then((r) => r.data),
    // Iter88 EmpBatch Follow-up A2 — employer picks an existing open
    // interview slot and reschedules the candidate. Atomic seat swap
    // + Wave-2-gated candidate email.
    reschedule: (appId, { slot_id, note = "" }) =>
        api.post(`/applications/${appId}/reschedule`, { slot_id, note }).then((r) => r.data),
    // Batch 4 (2026-02-28) — Assign / swap / remove interviewers on a
    // booked slot WITHOUT rescheduling.
    eligiblePanel: () => api.get(`/team/eligible-panel`).then((r) => r.data),
    assignInterviewers: (appId, interviewers) =>
        api.patch(`/applications/${appId}/interviewers`, { interviewers }).then((r) => r.data),
    // Batch 16.2 (2026-03-02) — employer candidate search by email or
    // mobile. POST so the term never appears in URL / access logs /
    // referer. Exact-match, employer-scoped, INTERVIEWER 403.
    candidateSearch: (query) =>
        api.post(`/pipeline/candidate-search`, { query }).then((r) => r.data),
    // Batch A3 (2026-03-02) — Started/Incomplete leads for a job.
    // RBAC: ACCOUNT_ADMIN + RECRUITER only (server-enforced).
    startedLeads: (jobId) =>
        api.get(`/pipeline/${jobId}/started`).then((r) => r.data),
    addresses: () => api.get("/employer/profile/addresses").then((r) => r.data),
    // Iter-Brief-UI (2026-03-06) — GET the Interviewer AI Brief. The
    // endpoint returns { application_id, brief, is_binding } for a
    // fully classified app, { application_id, brief: null, reason } for
    // apps with no signal yet, 503 when the kill-switch is OFF, 403
    // when the caller isn't authorised, 404 for unknown apps. The
    // panel component treats every non-200 as "hide gracefully" per
    // the read-only spec.
    interviewerBrief: (appId) =>
        api.get(`/applications/${appId}/interviewer-brief`).then((r) => r.data),
};

// ---------- Phase 3b-4: Scorecards + final decision ----------
export const scorecardApi = {
    // Public (interviewer magic link)
    resolve: (token) => api.post(`/scorecards/token/${token}`).then((r) => r.data),
    submit: (payload) => api.post("/scorecards", payload).then((r) => r.data),
    // Employer (drawer)
    list: (appId) => api.get(`/applications/${appId}/scorecards`).then((r) => r.data),
    synthesise: (appId) => api.post(`/applications/${appId}/scorecard-synthesis`).then((r) => r.data),
    finalDecision: (appId, outcome) =>
        api.patch(`/applications/${appId}/final-decision`, { outcome }).then((r) => r.data),
    revertDecision: (appId) =>
        api.post(`/applications/${appId}/revert-decision`).then((r) => r.data),
    // Iter67 — in-product recording + email resend.
    // Batch 3.1 (2026-02-XX) — explicit 20s timeout so a truly-stalled
    // request surfaces as a clean timeout error in the modal's inline
    // banner instead of the default 60s spin. Backend responds in <2s
    // on the happy path; 20s is a generous ceiling.
    submitSelf: (appId, payload) =>
        api.post(`/applications/${appId}/scorecards/self`, payload,
                 { timeout: 20000 }).then((r) => r.data),
    resend: (appId, interviewerEmail) =>
        api.post(`/applications/${appId}/scorecards/resend`, { interviewer_email: interviewerEmail }).then((r) => r.data),
};

// ---------- Batch 6 Phase A (2026-02-28) — Suggestive JD (draft-from-seed) ----------
export const draftSeedApi = {
    start: (seed) => api.post("/jobs/draft-from-seed", seed).then((r) => r.data),
    poll: (jobId) => api.get(`/jobs/draft-from-seed/${jobId}`).then((r) => r.data),
    commit: (draft) => api.post("/jobs/from-seed-draft", { draft }).then((r) => r.data),
    // Batch 6.1 (2026-02-28) — LOCK-guarded post-commit MCQ editor.
    // Returns { ok, mcq, attempts } on success; 400 on validation, 409
    // (SCREENING_LIVE) once any candidate has started/submitted the kit.
    editMcqs: (jobId, mcq) =>
        api.patch(`/jobs/${jobId}/screening/mcqs`, { mcq }).then((r) => r.data),
    // Batch 6.1 (2026-02-28) — "Preview as candidate" — options in the
    // deterministic delivery-shuffle order, correct_index stripped.
    previewShuffle: (mcq) =>
        api.post(`/screening/preview-shuffle`, { mcq }).then((r) => r.data),
    // Batch 16 (2026-03-02) — regenerate ONLY the MCQ block at a chosen
    // difficulty. Server returns { mcq: [...], difficulty }. Caller
    // replaces its current mcq array and keeps the previous set in
    // memory for undo. 180s timeout — LLM path.
    regenerateMcqs: (seed, mcqCount) =>
        api.post(`/jobs/draft-from-seed/regenerate-mcqs`, {
            seed, mcq_count: mcqCount || 0,
        }, { timeout: 180000 }).then((r) => r.data),
};

// ---------- Batch 3 (2026-02-28) — Interviewer role dashboard ----------
export const interviewerApi = {
    me: () => api.get("/interviewer/me").then((r) => r.data),
    myAssignments: () => api.get("/interviewer/my-assignments").then((r) => r.data),
};

// ---------- Phase 3b-1: Screening Plan Builder (employer) ----------
export const screeningPlanApi = {
    session: (jobId) => api.post("/screening/plan/session", { job_id: jobId }).then((r) => r.data),
    send: (jobId, message) => api.post("/screening/plan/send", { job_id: jobId, message }, { timeout: 180000 }).then((r) => r.data),
    generate: (jobId) => api.post("/screening/plan/generate", { job_id: jobId }, { timeout: 30000 }).then((r) => r.data),
    confirm: (jobId) => api.post("/screening/plan/confirm", { job_id: jobId }).then((r) => r.data),
    skip: (jobId, reason) => api.post("/screening/plan/skip", { job_id: jobId, reason }).then((r) => r.data),
    get: (jobId) => api.get(`/screening/plan/${jobId}`).then((r) => r.data),
};

// ---------- Phase 3b-1: Candidate MCQ + AV task ----------
export const mcqApi = {
    getPlan: (appId, token) => api.get(`/public/applications/${appId}/screening-plan`, candidateAuthConfig(token)).then((r) => r.data),
    submit: (appId, token, answers) => api.post(`/public/applications/${appId}/submit-mcq`, { answers }, candidateAuthConfig(token)).then((r) => r.data),
};
export const avTaskApi = {
    // In-session A/V desync fix (2026-03-03) — accept optional query
    // params so the SPA can cache-bust the resolve call during the
    // tight in-session poll window after a submit. Server already
    // sets Cache-Control: no-store, but a query-param nudge is a
    // belt-and-braces guard against edge/proxy caches and browser
    // back-forward cache serving a stale response.
    resolve: (token, params = null) => {
        const qs = params && typeof params === "object"
            ? "?" + new URLSearchParams(
                Object.entries(params).reduce((acc, [k, v]) => {
                    if (v !== undefined && v !== null) acc[k] = String(v);
                    return acc;
                }, {}),
            ).toString()
            : "";
        return api.get(`/public/av-task/${token}${qs}`).then((r) => r.data);
    },
    // Iter 55.1c-fix — AV upload was hitting axios's default 60s timeout
    // on 4G India (a 60s video @ 720p can easily exceed 60s at 200-400
    // KB/s upstream). Bump to 8 minutes so the network — not the client
    // — decides when to give up. Support optional progress callback so
    // the UI can render a bar instead of a silent spinner.
    upload: (token, questionId, blob, onProgress, opts = {}) => {
        const form = new FormData();
        const kind = blob.type?.startsWith("video/") ? "video" : "audio";
        const ext = blob.type?.includes("mp4") ? ".mp4" : (kind === "video" ? ".webm" : ".webm");
        form.append("file", blob, `answer${ext}`);
        // Iter55.2 Phase 1 — when running inside the McqTakePage inline
        // recorder we pass ?inline=1 so the backend suppresses the in-flow
        // booking email (the slot picker renders inline on ResultPage).
        const qs = new URLSearchParams({ question_id: questionId });
        if (opts.inline) qs.set("inline", "1");
        return api.post(
            `/public/av-task/${token}/upload?${qs.toString()}`,
            form,
            {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 480000, // 8 minutes — longest realistic upload on Slow-4G in India
                onUploadProgress: (evt) => {
                    if (typeof onProgress === "function" && evt?.total) {
                        onProgress(Math.round((evt.loaded / evt.total) * 100));
                    }
                },
            },
        ).then((r) => r.data);
    },

    // Iter56 Candidate Sprint 1 · Phase 1 · Task 3 — resilient chunked upload.
    // Three endpoints: init returns an upload_id, put streams a chunk, and
    // complete assembles + persists. Every call has its own retry loop
    // driven by uploadResilient() in AvTaskPage. Each chunk POST times out
    // at 90s so a stuck request fails fast and the retry-with-backoff
    // wrapper takes over.
    chunkInit: (token, payload) =>
        api.post(`/public/av-task/${token}/chunk-init`, payload, { timeout: 30000 })
            .then((r) => r.data),
    chunkPut: (token, { upload_id, question_id, chunk_index, total_chunks, chunk }) => {
        const form = new FormData();
        form.append("upload_id", upload_id);
        form.append("question_id", question_id);
        form.append("chunk_index", String(chunk_index));
        form.append("total_chunks", String(total_chunks));
        form.append("chunk", chunk, `chunk-${chunk_index}.bin`);
        return api.post(`/public/av-task/${token}/chunk`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 90000,
        }).then((r) => r.data);
    },
    chunkComplete: (token, payload) =>
        api.post(`/public/av-task/${token}/chunk-complete`, payload, { timeout: 60000 })
            .then((r) => r.data),

    // Batch 3.2 (2026-02-XX) — async commit/status split. `chunk-commit`
    // returns 202 within ~100ms (queues a background assembly), and
    // `chunk-status` is a lightweight poll for the outcome. Moves the
    // heavy reassembly off the request path so Cloudflare's ~100s cap
    // stops killing responses. Backwards-compat: `chunk-complete` still
    // works (it now delegates to commit+poll internally, bounded to ~8s
    // for older clients).
    //
    // Timeouts are short — commit is a metadata write, status is a
    // single Mongo read. If either takes >10s something is wrong at the
    // origin and the client-side retry loop should surface that.
    chunkCommit: (token, payload) =>
        api.post(`/public/av-task/${token}/chunk-commit`, payload,
                 { timeout: 15000 }).then((r) => r.data),
    chunkStatus: (token, uploadId) =>
        api.get(`/public/av-task/${token}/chunk-status/${uploadId}`,
                { timeout: 10000 }).then((r) => r.data),

    // Batch iOS-P1 (2026-03-02) — lightweight per-question transcode
    // status poll. The client polls this every ~1.5s for up to ~20s
    // after a deferred=true submit response, to catch the background
    // MP4 transcode finishing before showing the amber
    // "we couldn't fully process it" copy. Cheap enough to use with
    // short timeouts (single Mongo doc read + no writes).
    processingStatus: (token, questionId) =>
        api.get(`/public/av-task/${token}/processing-status/${questionId}`,
            { timeout: 8000 }).then((r) => r.data),

    // Iter70 (2026-02-22) — candidate-driven re-record after a deferred
    // submission. Server verifies the submission is EXPLICITLY deferred
    // (not healthy, not permanently-failed), parks the unplayable blob
    // for GC, and clears the av_submissions entry so a fresh chunk-init
    // can start.
    reRecord: (token, { question_id }) =>
        api.post(`/public/av-task/${token}/re-record`, { question_id },
            { timeout: 20000 }).then((r) => r.data),

    // Item 6 fix (2026-02-21) — client uploader signals "I've permanently
    // given up" so the ops board's AV_UPLOAD_FAILED blocker can surface the
    // trapped candidate. Fire-and-forget: prefer sendBeacon so it survives
    // page unload; fall back to fetch keepalive.
    reportUploadFailed: (token, { question_id, upload_id, reason }) => {
        const body = JSON.stringify({ question_id, upload_id, reason: (reason || "client_gave_up").slice(0, 200) });
        const url = `${API}/public/av-task/${token}/upload-failed`;
        try {
            if (typeof navigator !== "undefined"
                && typeof navigator.sendBeacon === "function") {
                const blob = new Blob([body], { type: "application/json" });
                const ok = navigator.sendBeacon(url, blob);
                if (ok) return Promise.resolve({ ok: true, transport: "beacon" });
            }
        } catch { /* fall through to fetch */ }
        try {
            return fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body,
                keepalive: true,
            }).then(() => ({ ok: true, transport: "fetch" })).catch(() => ({ ok: false }));
        } catch {
            return Promise.resolve({ ok: false });
        }
    },
};

// ---------- Iter55.2 Phase 1: Apply-intent capture (public) ----------
export const applyIntentsApi = {
    create: (email, slug, utm = {}) => {
        // Phase C2 — pick up the session-scoped self-check attribution
        // flag written by the SelfCheckWidget when a candidate completes
        // the check on this slug. Consumed once, then cleared.
        let afterSelfCheck = false;
        try {
            const flag = sessionStorage.getItem("bmi_self_check_completed_slug");
            if (flag && flag === slug) {
                afterSelfCheck = true;
                sessionStorage.removeItem("bmi_self_check_completed_slug");
            }
        } catch { /* sessionStorage unavailable — count as no attribution */ }
        return api.post("/public/apply-intents", {
            email, slug, ...utm, after_self_check: afterSelfCheck,
        }).then((r) => r.data);
    },
    progress: (intentId, resume_step, extras = {}) =>
        api.patch(`/public/apply-intents/${intentId}`, { resume_step, ...extras }).then((r) => r.data),
    lookup: (email, slug) =>
        api.get(`/public/apply-intents?email=${encodeURIComponent(email)}&slug=${encodeURIComponent(slug)}`).then((r) => r.data),
};

// ---------- Interviews ----------
export const interviewsApi = {
    cancel: (appId, reason) => api.post(`/interviews/${appId}/cancel`, { reason }).then((r) => r.data),
};

// ---------- Auditor ----------
export const auditorApi = {
    stats: () => api.get("/auditor/stats").then((r) => r.data),
    pending: () => api.get("/auditor/pending").then((r) => r.data),
    reviewed: (limit = 100) => api.get(`/auditor/reviewed?limit=${limit}`).then((r) => r.data),
    approve: (appId, note) => api.post(`/auditor/${appId}/approve`, { note }).then((r) => r.data),
    reject: (appId, reason) => api.post(`/auditor/${appId}/reject`, { reason }).then((r) => r.data),
};

// ---------- Employer team (multi-user) ----------
export const teamApi = {
    pending: () => api.get("/employer/team/pending").then((r) => r.data),
    members: () => api.get("/employer/team/members").then((r) => r.data),
    approve: (userId) => api.post(`/employer/team/${userId}/approve`).then((r) => r.data),
    reject: (userId) => api.post(`/employer/team/${userId}/reject`).then((r) => r.data),
    remove: (userId) => api.delete(`/employer/team/${userId}`).then((r) => r.data),
};

// ---------- New: invite-based team (Phase 2e-3) ----------
export const inviteApi = {
    list: () => api.get("/employer/team").then((r) => r.data),
    invite: (payload) => api.post("/employer/team/invite", payload).then((r) => r.data),
    updateRole: (id, payload) => api.patch(`/employer/team/${id}/role`, payload).then((r) => r.data),
    assignJobs: (id, job_ids) => api.post(`/employer/team/${id}/assign-jobs`, { job_ids }).then((r) => r.data),
    remove: (id) => api.delete(`/employer/team/${id}`).then((r) => r.data),
    reactivate: (id) => api.post(`/employer/team/${id}/reactivate`).then((r) => r.data),
    resendInviteByEmail: (email) => api.post("/employer/team/resend-invite-by-email", { email }).then((r) => r.data),
    accept: (token, full_name, password) => api.post("/employer/team/accept-invite", { token, full_name, password }).then((r) => r.data),
    previewInvite: (token) => api.get(`/employer/team/accept-invite/preview?token=${encodeURIComponent(token)}`).then((r) => r.data),
};

// ---------- GST certificate ----------
export const gstApi = {
    upload: (file) => {
        const fd = new FormData(); fd.append("gst_file", file);
        return api.post("/employer/gst-certificate", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    remove: () => api.delete("/employer/gst-certificate").then((r) => r.data),
};

// OG image URL for a public job slug (used in <meta og:image>)
export function ogImageUrl(slug) {
    return `${API}/og/jobs/${slug}`;
}

// Absolute URL for a stored /uploads/... asset (logo, brochure, OG image, etc.)
// The backend now serves uploads via /api/uploads/* (auth-checked). We accept
// legacy stored paths of the form '/uploads/...' and rewrite them here.
export function assetUrl(relativeOrAbsolute) {
    if (!relativeOrAbsolute) return "";
    if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
    let path = relativeOrAbsolute;
    if (path.startsWith("/uploads/")) {
        path = "/api/uploads/" + path.slice("/uploads/".length);
    }
    return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

// ---------- Candidate auth + profile ----------
export const candidateApi = {
    register: (payload) => api.post("/candidates/register", payload).then((r) => r.data),
    verifyOtp: (email, code) => api.post("/candidates/verify-otp", { email, code }).then((r) => r.data),
    resendOtp: (email) => api.post("/candidates/resend-otp", { email }).then((r) => r.data),
    login: (email, password) => api.post("/candidates/login", { email, password }).then((r) => r.data),
    logout: () => api.post("/candidates/logout").then((r) => r.data),
    me: () => api.get("/candidates/me").then((r) => r.data.candidate),
    updateMe: (payload) => api.patch("/candidates/me", payload).then((r) => r.data.candidate),
    myApplications: () => api.get("/candidates/my-applications").then((r) => r.data),
    uploadCv: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/candidates/me/cv", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    recommendations: () => api.get("/candidates/recommendations").then((r) => r.data),
};


// ---------- Batch iOS-P2 (2026-03-02) — Help & Feedback bot ----------
// Dual-realm capture endpoint + FAQ fetch + admin listing/status.
// The client fires PostHog directly from posthog_event in the response
// (server never sees the PostHog project key).
//
// Batch iOS-P2.1 (2026-03-02) EXTENSION — multipart submit path for
// screenshot attachments + attachment URL builder + admin reply.
export const supportApi = {
    faq: (realm) =>
        api.get(`/support/faq`, { params: { realm: realm || "any" } })
            .then((r) => r.data?.faq || []),
    // JSON-only submit — kept for backward compat with any consumer
    // that doesn't attach files. Widget uses submitWithAttachments()
    // when files are present.
    submit: (payload) =>
        api.post(`/support/feedback`, payload).then((r) => r.data),
    // Multipart submit — accepts { ...fields, attachments: File[] }.
    // FormData built inline so the axios `Content-Type` header is
    // auto-generated with the correct boundary.
    submitWithAttachments: (payload, files) => {
        const fd = new FormData();
        Object.entries(payload || {}).forEach(([k, v]) => {
            if (v !== null && v !== undefined) fd.append(k, String(v));
        });
        (files || []).forEach((f) => fd.append("attachments", f, f.name));
        return api.post(`/support/feedback`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        }).then((r) => r.data);
    },
    // Absolute URL for an attachment thumbnail / full image. Cookie-
    // authenticated (candidate, employer, or admin — server picks).
    attachmentUrl: (fbId, index) =>
        `${BACKEND_URL}/api/support/attachment/${fbId}/${index}`,
    // Ops surface
    adminList: (params) =>
        api.get(`/admin/support/feedback`, { params }).then((r) => r.data),
    adminSetStatus: (id, status) =>
        api.patch(`/admin/support/feedback/${id}/status`, { status }).then((r) => r.data),
    adminReply: (id, reply, sendEmail = true) =>
        api.post(`/admin/support/feedback/${id}/reply`,
                 { reply, send_email: sendEmail }).then((r) => r.data),
};
